// /apps/patient/src/components/dashboard/TodaysWorkoutCard.tsx

import { useNavigate } from 'react-router-dom';
import { Dumbbell, PlayCircle, Calendar, Clock, CheckCircle, Eye } from 'lucide-react';
import { format } from 'date-fns';

interface TodayWorkout {
  id: string;
  programName: string;
  workoutName?: string;
  exerciseCount: number;
  estimatedMinutes?: number;
  assignmentType: 'workout_plan' | 'rehab_program' | 'exercise_prescription';
  prescriptionId?: string;
  isCompleted?: boolean;
  sessionId?: string;
}

interface TodaysWorkoutCardProps {
  workout: TodayWorkout | null;
  loading?: boolean;
  hasActivePlan?: boolean;
}

export function TodaysWorkoutCard({ workout, loading, hasActivePlan = true }: TodaysWorkoutCardProps) {
  const navigate = useNavigate();
  const today = format(new Date(), 'yyyy-MM-dd');

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
        <div className="h-5 w-32 bg-gray-200 rounded mb-4" />
        <div className="h-8 w-48 bg-gray-200 rounded mb-2" />
        <div className="h-4 w-24 bg-gray-200 rounded mb-6" />
        <div className="h-12 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  if (!workout && !hasActivePlan) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center text-gray-400 mb-2">
          <Calendar className="h-4 w-4 mr-2" />
          <span className="text-sm font-medium">Today</span>
        </div>

        <div className="text-center py-6">
          <div className="inline-flex p-4 rounded-full bg-primary/10 mb-4">
            <Dumbbell className="h-8 w-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No plan yet</h3>
          <p className="text-sm text-gray-500">Build a plan around your answers, or start with a simple routine.</p>
        </div>

        <button
          onClick={() => navigate('/programs')}
          className="w-full mt-4 px-4 py-3 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
        >
          Build my plan
        </button>
      </div>
    );
  }

  if (!workout) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center text-gray-400 mb-2">
          <Calendar className="h-4 w-4 mr-2" />
          <span className="text-sm font-medium">Today</span>
        </div>

        <div className="text-center py-6">
          <div className="inline-flex p-4 rounded-full bg-gray-100 mb-4">
            <Dumbbell className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Rest Day</h3>
          <p className="text-sm text-gray-500">No workout scheduled for today</p>
        </div>

        <button
          onClick={() => navigate('/calendar')}
          className="w-full mt-4 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          View Schedule
        </button>
      </div>
    );
  }

  const handleStartWorkout = () => {
    navigate(`/programs/${workout.id}/session/${today}`);
  };

  const handleViewDetails = () => {
    if (workout.sessionId) {
      navigate(`/progress/session/${workout.sessionId}`);
    }
  };

  // Completed state
  if (workout.isCompleted) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-green-200">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center text-green-600">
            <CheckCircle className="h-4 w-4 mr-2" />
            <span className="text-sm font-medium">Completed Today</span>
          </div>
          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
            Done
          </span>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-1">
          {workout.workoutName || workout.programName}
        </h3>

        {workout.workoutName && (
          <p className="text-sm text-gray-500 mb-2">{workout.programName}</p>
        )}

        <div className="flex items-center gap-4 text-sm text-gray-600 mb-6">
          <span className="flex items-center">
            <Dumbbell className="h-4 w-4 mr-1" />
            {workout.exerciseCount} exercise{workout.exerciseCount !== 1 ? 's' : ''}
          </span>
          {workout.estimatedMinutes && (
            <span className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              ~{workout.estimatedMinutes} min
            </span>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleViewDetails}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-50 text-green-700 font-medium rounded-lg hover:bg-green-100 transition-colors"
          >
            <Eye className="h-5 w-5" />
            View Details
          </button>
          <button
            onClick={handleStartWorkout}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
          >
            <PlayCircle className="h-5 w-5" />
            Do Again
          </button>
        </div>
      </div>
    );
  }

  // Default: not completed state
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center text-primary mb-2">
        <Calendar className="h-4 w-4 mr-2" />
        <span className="text-sm font-medium">Today's Workout</span>
      </div>

      <h3 className="text-xl font-bold text-gray-900 mb-1">
        {workout.workoutName || workout.programName}
      </h3>

      {workout.workoutName && (
        <p className="text-sm text-gray-500 mb-2">{workout.programName}</p>
      )}

      <div className="flex items-center gap-4 text-sm text-gray-600 mb-6">
        <span className="flex items-center">
          <Dumbbell className="h-4 w-4 mr-1" />
          {workout.exerciseCount} exercise{workout.exerciseCount !== 1 ? 's' : ''}
        </span>
        {workout.estimatedMinutes && (
          <span className="flex items-center">
            <Clock className="h-4 w-4 mr-1" />
            ~{workout.estimatedMinutes} min
          </span>
        )}
      </div>

      <button
        onClick={handleStartWorkout}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors"
      >
        <PlayCircle className="h-5 w-5" />
        Start Workout
      </button>
    </div>
  );
}
